import type { AudioEngine } from '../audio/audio.ts';

/**
 * Dois cursores de volume, no canto do mundo.
 *
 * Separados — efeitos e música — porque as duas queixas sobre áudio em jogo são opostas e
 * frequentes: "a música incomoda" e "não escuto o golpe". Um cursor só obrigaria a escolher
 * entre as duas, e a escolha de quem só quer baixar a música seria desligar tudo.
 *
 * Fica sobre o cenário e não na barra lateral porque é ajuste, não informação de jogo: ele
 * é mexido uma vez por sessão e não compete com o inventário por espaço permanente.
 *
 * No toque ele nasce **escondido** e abre pelo menu (ver `input/touch.ts`). O canto onde ele
 * mora passou a ser o do botão do menu, e entre os dois quem cede é este: numa tela de
 * celular, um controle que se mexe uma vez por sessão não paga o pedaço de cenário que ocupa
 * o tempo todo — e é o mesmo argumento que o pôs sobre o mundo no computador, onde o pedaço
 * de cenário é barato.
 */
export function mountAudioControls(root: HTMLElement, audio: AudioEngine): AudioControls {
  root.className = 'audio-controls';
  root.innerHTML = '';

  root.append(
    slider('som', audio.masterVolume, (value) => audio.setMasterVolume(value)),
    slider('mús', audio.musicVolume, (value) => audio.setMusicVolume(value)),
  );

  return {
    toggle: () => root.classList.toggle('open'),
  };
}

export interface AudioControls {
  /** Mostra ou esconde os cursores. Só tem efeito onde o CSS os esconde — ver acima. */
  toggle(): void;
}

function slider(
  label: string,
  initial: number,
  onChange: (value: number) => void,
): HTMLElement {
  const wrapper = document.createElement('label');
  wrapper.className = 'audio-label';
  wrapper.textContent = label;

  const input = document.createElement('input');
  input.type = 'range';
  input.min = '0';
  input.max = '100';
  input.value = String(Math.round(initial * 100));
  input.className = 'audio-slider';

  // `input` e não `change`: o volume acompanha o cursor enquanto ele é arrastado, que é a
  // única forma de ajustar áudio pelo ouvido em vez de por tentativa.
  input.addEventListener('input', () => onChange(Number(input.value) / 100));

  // O cursor de volume não pode roubar o teclado do jogo: quem o arrasta e volta a andar
  // não espera que as setas continuem mexendo nele.
  input.addEventListener('change', () => input.blur());

  wrapper.append(input);
  return wrapper;
}
